/**
 * Admin Dashboard - Main Container
 * Extracted from admin.tsx per ADR-039: Component Size Strategy
 *
 * This is the main container component for the admin dashboard.
 * It provides tab navigation and delegates rendering to specialized tab components.
 *
 * Original: 1516 lines (51% over ADR-039 threshold)
 * Refactored: ~176 lines (85% under ADR-039 threshold)
 *
 * ADR-039 Compliance: ✅ PASS (Layout component < 1000 lines)
 */

import {
  Alert,
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import {
  FiActivity,
  FiAlertTriangle,
  FiHome,
  FiKey,
  FiSettings,
  FiShield,
  FiUsers,
} from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import { FeatureSuspense, LazyHealthDashboard } from "@/components/lazy"
import { useUserWithPermissions } from "@/hooks/usePermissions"

import { HouseholdsManagementTab } from "./tabs/HouseholdsManagementTab"
import { PermissionManagementTab } from "./tabs/PermissionManagementTab"
import { RoleManagementTab } from "./tabs/RoleManagementTab"
import { SystemOverviewTab } from "./tabs/SystemOverviewTab"
// Import tab components
import { UserManagementTab } from "./tabs/UserManagementTab"

export function AdminDashboard() {
  const { isAdmin, user, isLoading, isAuthenticated, scopes } =
    useUserWithPermissions()
  const [activeTab, setActiveTab] = useState("user-management")

  // Show authentication debug info
  if (!isAuthenticated) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert.Root status="warning">
          <Alert.Indicator>
            <FiAlertTriangle />
          </Alert.Indicator>
          <Box>
            <Alert.Title>Authentication Required!</Alert.Title>
            <Alert.Description>
              You are not authenticated. Please log in to access the admin
              dashboard.
            </Alert.Description>
          </Box>
        </Alert.Root>
      </Container>
    )
  }

  // Show authorization debug info
  if (!isAdmin()) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert.Root status="error">
          <Alert.Indicator>
            <FiAlertTriangle />
          </Alert.Indicator>
          <Box>
            <Alert.Title>Access Denied!</Alert.Title>
            <Alert.Description>
              You need administrator privileges to access this page.
              <br />
              Current user: {user?.email}
              <br />
              Scopes: {scopes.join(", ") || "None"}
            </Alert.Description>
          </Box>
        </Alert.Root>
      </Container>
    )
  }

  const tabs = [
    { id: "user-management", label: "User Management", icon: FiUsers },
    { id: "households", label: "Households", icon: FiHome },
    { id: "roles", label: "Roles", icon: FiShield },
    { id: "permissions", label: "Permissions", icon: FiKey },
    { id: "health", label: "Health Monitor", icon: FiActivity },
    { id: "system-overview", label: "System Overview", icon: FiSettings },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case "user-management":
        return <UserManagementTab />
      case "households":
        return <HouseholdsManagementTab />
      case "roles":
        return <RoleManagementTab />
      case "permissions":
        return <PermissionManagementTab />
      case "health":
        return (
          <FeatureSuspense>
            <LazyHealthDashboard />
          </FeatureSuspense>
        )
      case "system-overview":
        return <SystemOverviewTab />
      default:
        return <UserManagementTab />
    }
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Stack align="start" gap={2}>
          <Heading size="xl">
            {isLoading ? "Loading..." : "Admin Dashboard"}
          </Heading>
          <Text color="gray.600">
            Comprehensive system administration and RBAC management
          </Text>
          <Flex gap={2} align="center">
            <Badge colorScheme="blue" fontSize="sm">
              Admin Only
            </Badge>
            <Badge colorScheme="purple" fontSize="sm">
              RBAC Enabled
            </Badge>
          </Flex>
        </Stack>
        <FiShield size={32} color="gray" />
      </Flex>

      {/* Tab Navigation */}
      <Flex
        gap={2}
        mb={6}
        borderBottom="1px"
        borderColor="gray.200"
        pb={2}
        overflowX="auto"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "solid" : "ghost"}
              colorScheme={activeTab === tab.id ? "blue" : "gray"}
              onClick={() => setActiveTab(tab.id)}
              size="sm"
              flexShrink={0}
              gap={2}
            >
              <Icon />
              {tab.label}
            </Button>
          )
        })}
      </Flex>

      {/* Tab Content */}
      <Box>{renderTabContent()}</Box>
    </Container>
  )
}
